// third-party
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// project imports
import axios2 from 'utils/axios2';
import apiSlice from './apiSlice';
import { hasPermission, getUserContext, createAuditEntry } from '../../utils/rbac';

// ----------------------------------------------------------------------

export const fetchStorageOverview = createAsyncThunk('datahubStorage/fetchOverview', async (projectId, { dispatch, rejectWithValue }) => {
    try {
        const { user_token, user_permissions, user_groups, user_id } = getUserContext();
        if (!hasPermission(user_permissions, 'storage_analytics', 'view_usage')) {
            throw new Error('Insufficient permissions: Cannot view storage overview');
        }
        const data = await dispatch(
            apiSlice.endpoints.getDatahubStorageOverview.initiate({
                projectId,
                user_token,
                permissions: user_permissions,
                groups: user_groups
            })
        ).unwrap();
        createAuditEntry(user_id, 'fetch_storage_overview', 'storage', projectId, true);
        return data;
    } catch (error) {
        const { user_id } = getUserContext();
        createAuditEntry(user_id, 'fetch_storage_overview', 'storage', projectId, false, error.message);
        return rejectWithValue(error?.data || error.response?.data || error.message);
    }
});

export const uploadFile = createAsyncThunk('datahubStorage/uploadFile', async ({ projectId, file, path }, { dispatch, rejectWithValue }) => {
    try {
        const { user_token, user_permissions, user_groups, user_id } = getUserContext();
        if (!hasPermission(user_permissions, 'files', 'upload')) {
            throw new Error('Insufficient permissions: Cannot upload files');
        }
        const data = await dispatch(
            apiSlice.endpoints.uploadDatahubFile.initiate({
                projectId,
                file,
                path: path || '',
                user_token,
                permissions: user_permissions,
                groups: user_groups,
                audit: {
                    action: 'upload_file',
                    resource_type: 'files',
                    details: { filename: file.name, path: path || '', size: file.size }
                }
            })
        ).unwrap();
        createAuditEntry(user_id, 'upload_file', 'files', data?.id ?? 'new', true);
        return data;
    } catch (error) {
        const { user_id } = getUserContext();
        createAuditEntry(user_id, 'upload_file', 'files', 'new', false, error.message);
        return rejectWithValue(error?.data || error.response?.data || error.message);
    }
});

export const downloadFile = createAsyncThunk('datahubStorage/downloadFile', async ({ projectId, fileId }, { dispatch, rejectWithValue }) => {
    try {
        const { user_token, user_permissions, user_groups, user_id } = getUserContext();
        if (!hasPermission(user_permissions, 'files', 'download')) {
            throw new Error('Insufficient permissions: Cannot download files');
        }
        const blob = await dispatch(
            apiSlice.endpoints.getDatahubStorageDownload.initiate({
                projectId,
                fileId,
                user_token,
                permissions: user_permissions,
                groups: user_groups
            })
        ).unwrap();
        createAuditEntry(user_id, 'download_file', 'files', fileId, true);
        return { fileId, blob };
    } catch (error) {
        const { user_id } = getUserContext();
        createAuditEntry(user_id, 'download_file', 'files', fileId, false, error.message);
        return rejectWithValue(error?.data || error.response?.data || error.message);
    }
});

export const deleteFile = createAsyncThunk('datahubStorage/deleteFile', async ({ projectId, fileId }, { rejectWithValue }) => {
    try {
        const { user_token, user_permissions, user_groups, user_id } = getUserContext();

        // Check delete permission
        if (!hasPermission(user_permissions, 'files', 'delete')) {
            throw new Error('Insufficient permissions: Cannot delete files');
        }

        await axios2.delete(`/datahub/storage/files/${fileId}`, {
            params: {
                projectId,
                user_token,
                permissions: user_permissions,
                groups: user_groups,
                audit: {
                    action: 'delete_file',
                    resource_type: 'files',
                    details: { file_id: fileId }
                }
            }
        });

        // Create audit entry
        const auditEntry = createAuditEntry(user_id, 'delete_file', 'files', fileId, true);

        return fileId;
    } catch (error) {
        // Create audit entry for failed operation
        const { user_id } = getUserContext();
        const auditEntry = createAuditEntry(user_id, 'delete_file', 'files', fileId, false, error.message);

        return rejectWithValue(error.response?.data || error.message);
    }
});

export const fetchFiles = createAsyncThunk('datahubStorage/fetchFiles', async ({ projectId, path = '/' }, { rejectWithValue }) => {
    try {
        const { user_token, user_permissions, user_groups, user_id } = getUserContext();

        // Check view permission
        if (!hasPermission(user_permissions, 'files', 'view')) {
            throw new Error('Insufficient permissions: Cannot view files');
        }

        const response = await axios2.get('/datahub/storage/files', {
            params: {
                projectId,
                path,
                user_token,
                permissions: user_permissions,
                groups: user_groups
            }
        });

        // Create audit entry
        const auditEntry = createAuditEntry(user_id, 'fetch_files', 'files', path, true);

        return response.data;
    } catch (error) {
        // Create audit entry for failed operation
        const { user_id } = getUserContext();
        const auditEntry = createAuditEntry(user_id, 'fetch_files', 'files', path, false, error.message);

        return rejectWithValue(error.response?.data || error.message);
    }
});

export const createFolder = createAsyncThunk(
    'datahubStorage/createFolder',
    async ({ projectId, folderName, path }, { rejectWithValue }) => {
        try {
            const { user_token, user_permissions, user_groups, user_id } = getUserContext();

            // Check folder create permission
            if (!hasPermission(user_permissions, 'folders', 'create')) {
                throw new Error('Insufficient permissions: Cannot create folders');
            }

            const response = await axios2.post('/datahub/storage/folders', {
                projectId,
                folderName,
                path,
                user_token,
                permissions: user_permissions,
                groups: user_groups,
                audit: {
                    action: 'create_folder',
                    resource_type: 'folders',
                    details: {
                        folder_name: folderName,
                        path: path
                    }
                }
            });

            // Create audit entry
            const auditEntry = createAuditEntry(user_id, 'create_folder', 'folders', response.data.id, true);

            return response.data;
        } catch (error) {
            // Create audit entry for failed operation
            const { user_id } = getUserContext();
            const auditEntry = createAuditEntry(user_id, 'create_folder', 'folders', 'new', false, error.message);

            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// ----------------------------------------------------------------------

const initialState = {
    overview: null,
    files: [],
    currentPath: '/',
    loading: false,
    error: null,
    uploadProgress: 0,
    analytics: {
        totalFiles: 0,
        totalSize: 0,
        fileTypes: {},
        recentUploads: []
    }
};

// ----------------------------------------------------------------------

const slice = createSlice({
    name: 'datahubStorage',
    initialState,
    reducers: {
        setCurrentPath(state, action) {
            state.currentPath = action.payload;
        },
        setUploadProgress(state, action) {
            state.uploadProgress = action.payload;
        },
        clearError(state) {
            state.error = null;
        },
        addFile(state, action) {
            state.files.push(action.payload);
        },
        removeFile(state, action) {
            state.files = state.files.filter((file) => file.id !== action.payload);
        },
        clearFiles(state) {
            state.files = [];
        },
        updateFile(state, action) {
            const index = state.files.findIndex((file) => file.id === action.payload.id);
            if (index !== -1) {
                state.files[index] = action.payload;
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // fetchStorageOverview
            .addCase(fetchStorageOverview.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchStorageOverview.fulfilled, (state, action) => {
                state.loading = false;
                state.overview = action.payload;
                state.analytics = action.payload.analytics || state.analytics;
            })
            .addCase(fetchStorageOverview.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // uploadFile
            .addCase(uploadFile.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.uploadProgress = 0;
            })
            .addCase(uploadFile.fulfilled, (state, action) => {
                state.loading = false;
                state.uploadProgress = 100;
                state.files.push(action.payload);
            })
            .addCase(uploadFile.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.uploadProgress = 0;
            })
            // downloadFile
            .addCase(downloadFile.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(downloadFile.fulfilled, (state, action) => {
                state.loading = false;
                // Handle file download (create download link)
                const url = window.URL.createObjectURL(action.payload.blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `file_${action.payload.fileId}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .addCase(downloadFile.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // deleteFile
            .addCase(deleteFile.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteFile.fulfilled, (state, action) => {
                state.loading = false;
                state.files = state.files.filter((file) => file.id !== action.payload);
            })
            .addCase(deleteFile.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // fetchFiles
            .addCase(fetchFiles.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchFiles.fulfilled, (state, action) => {
                state.loading = false;
                state.files = action.payload.files || [];
                state.currentPath = action.payload.path || state.currentPath;
            })
            .addCase(fetchFiles.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // createFolder
            .addCase(createFolder.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createFolder.fulfilled, (state, action) => {
                state.loading = false;
                state.files.push(action.payload);
            })
            .addCase(createFolder.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

// ----------------------------------------------------------------------

// Reducer
export default slice.reducer;

// Actions
export const { setCurrentPath, setUploadProgress, clearError, addFile, removeFile, clearFiles, updateFile } = slice.actions;
